import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useRef } from 'react';
import { BuildingCompletionStatus, TravelerBuilding } from '../types';

interface BuildingCarouselProps {
  buildings: TravelerBuilding[];
  selectedBuildingId: number | null;
  onSelectBuilding: (buildingId: number) => void;
}

function getStatusIndicator(status: BuildingCompletionStatus) {
  switch (status) {
    case 'complete':
      return <CheckCircleIcon sx={{ color: '#27ae60', fontSize: 20 }} />;
    case 'in_progress':
      return (
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            bgcolor: '#f39c12',
            mx: 'auto',
          }}
        />
      );
    case 'no_trusses':
      return (
        <Tooltip title="No trusses assigned" placement="bottom">
          <ErrorOutlineIcon sx={{ color: '#e74c3c', fontSize: 20 }} />
        </Tooltip>
      );
    default:
      return (
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            border: '2px solid #bdc3c7',
            mx: 'auto',
          }}
        />
      );
  }
}

const BuildingCarousel = ({
  buildings,
  selectedBuildingId,
  onSelectBuilding,
}: BuildingCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        bgcolor: '#e8f4fc',
        border: '1px solid #b8d4e8',
        borderRadius: '4px',
        p: '16px 20px',
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <IconButton
        onClick={scrollLeft}
        size="small"
        sx={{
          border: '1px solid #999',
          bgcolor: '#fff',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
      >
        <ChevronLeftIcon />
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            mb: 1.5,
            textAlign: 'center',
            color: '#333',
          }}
        >
          {buildings.length} Building{buildings.length !== 1 ? 's' : ''}
        </Typography>
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#b8d4e8',
              borderRadius: 2,
            },
          }}
        >
          {buildings.map(building => (
            <Box
              key={building.id}
              onClick={() => onSelectBuilding(building.id)}
              sx={{
                flexShrink: 0,
                width: 80,
                p: 1,
                bgcolor: selectedBuildingId === building.id ? '#e0e0e0' : '#fff',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'background 0.2s',
                '&:hover': { bgcolor: '#f5f5f5' },
                borderRadius: 0,
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  border: '1px solid #333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 0.5,
                  bgcolor: '#fff',
                }}
              >
                <HomeWorkIcon sx={{ fontSize: 24, color: '#333' }} />
              </Box>
              <Tooltip title={building.name} placement="bottom">
                <Typography
                  noWrap
                  sx={{
                    fontSize: '10px',
                    fontWeight: 500,
                    color: building.completionStatus === 'complete' ? '#333' : '#e74c3c',
                    mb: 0.5,
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {building.name}
                </Typography>
              </Tooltip>
              <Box sx={{ textAlign: 'center' }}>
                {getStatusIndicator(building.completionStatus)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <IconButton
        onClick={scrollRight}
        size="small"
        sx={{
          border: '1px solid #999',
          bgcolor: '#fff',
          '&:hover': { bgcolor: '#f0f0f0' },
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

export default BuildingCarousel;
